import type {
	FilterDefinition,
	RangeFilterDefinition,
	RadioFilterDefinition,
	CheckboxFilterDefinition,
	SearchInputFilterDefinition,
	SelectFilterDefinition,
	RangeFilterState,
	SingleValueFilterState,
	MultiValueFilterState,
	UseFilterStateReturn,
} from '../types';
import { RangeFilter } from './RangeFilter';
import { RadioFilter } from './RadioFilter';
import { CheckboxFilter } from './CheckBoxFilter';
import { SearchInputFilter } from './SearchInputFilter';
import { SelectFilter } from './SelectFilter';

interface FilterOption {
	value: string;
	label: string;
}

interface FilterOptionGroup {
	label: string;
	options: FilterOption[];
}

interface Props {
	definition: FilterDefinition;
	filterState: UseFilterStateReturn;
	/** Resolved options for dynamic filters (from props like shapes, categories) */
	resolvedOptions?: FilterOption[];
	/** Resolved option groups for select filters (e.g. shelves grouped by rack) */
	resolvedOptionGroups?: FilterOptionGroup[];
	compact?: boolean;
}

/**
 * Renders the correct atomic filter component based on the definition type.
 * This is the "bridge" between declarative config and actual React components.
 */
export const FilterRenderer = ({
	definition,
	filterState,
	resolvedOptions,
	resolvedOptionGroups,
	compact = false,
}: Props) => {
	const { state, setFilter, applyFilter } = filterState;
	const currentState = state[definition.key];

	if (!currentState) return null;

	switch (definition.type) {
		case 'range': {
			const def = definition as RangeFilterDefinition;
			const rangeState = currentState as RangeFilterState;
			return (
				<RangeFilter
					value={rangeState.value}
					initialRange={rangeState.initialRange}
					onChange={(val) => setFilter(def.key, val)}
					onApply={() => applyFilter(def.key)}
					step={def.step}
					suffix={def.suffix}
					compact={compact}
				/>
			);
		}

		case 'radio': {
			const def = definition as RadioFilterDefinition;
			const radioState = currentState as SingleValueFilterState;
			return (
				<RadioFilter
					value={radioState.value}
					options={resolvedOptions || def.options || []}
					onChange={(val) => {
						if (val === 'all') {
							filterState.resetFilter(def.key);
						} else {
							filterState.setAndApply(def.key, val);
						}
					}}
					showAllOption={def.showAllOption}
					allOptionLabel={def.allOptionLabel}
					compact={compact}
				/>
			);
		}

		case 'checkbox': {
			const def = definition as CheckboxFilterDefinition;
			const checkState = currentState as MultiValueFilterState;

			const isDependencyMet = def.dependsOn
				? Boolean((state[def.dependsOn] as SingleValueFilterState)?.value)
				: true;

			const allOptions = resolvedOptions || def.options || [];

			return (
				<CheckboxFilter
					value={checkState.value}
					options={allOptions}
					onChange={(vals) => {
    filterState.setAndApply(def.key, vals);
}}
					searchable={def.searchable}
					searchPlaceholder="Search types"
					disabled={!isDependencyMet}
					disabledMessage={
						def.dependsOn ? 'Select a category first' : undefined
					}
					compact={compact}
					showSelectAll={false}
				/>
			);
		}

		case 'search-input': {
			const def = definition as SearchInputFilterDefinition;
			const inputState = currentState as SingleValueFilterState;
			return (
				<SearchInputFilter
					value={inputState.value}
					onChange={(val) => setFilter(def.key, val)}
					onApply={() => applyFilter(def.key)}
					placeholder={def.placeholder}
					compact={compact}
				/>
			);
		}

		case 'select': {
			const def = definition as SelectFilterDefinition;
			const selectState = currentState as MultiValueFilterState;
			return (
				<SelectFilter
					value={selectState.value}
					onChange={(vals) => filterState.setAndApply(def.key, vals)}
					onApply={() => applyFilter(def.key)}
					optionGroups={resolvedOptionGroups}
					options={resolvedOptions}
					placeholder={def.placeholder}
					mode={def.mode}
					compact={compact}
					showApplyButton={def.requireApply}
				/>
			);
		}

		default:
			return null;
	}
};
